import { BookMarked, FileBarChart, FolderOpen, LayoutGrid } from "lucide-react";
import { lazy } from "react";
import { Navigate } from "react-router-dom";

import { StaffEmailToggle } from "@/components/layout/StaffEmailToggle";
import { detectEnvironment } from "@/lib/environment";
import type { ModuleDefinition, ModuleSettingsToggle } from "@/lib/module-registry";
import type { AppRoute } from "@/lib/routes";
import {
  REPORTS,
  REPORTS_INDEX_PATH,
  reportPath,
} from "@/pages/reports/report-registry";
import { MY_REPORTS_PATH, STANDARD_REPORTS_PATH, useMyReportRows } from "@/pages/reports/useMyReportRows";

/**
 * Reports — the home of every report in the app: the ones a user builds
 * (custom reports, behind the `report_builder` tenant feature) and the canned
 * catalog.
 *
 * **The landing is the module's own Overview** (`/reports`): the tenant's
 * standard reports, the reader's own, and the catalog. It used to be skipped —
 * with one catalog report a catalog page was a click for nothing — and the
 * docblock here said to point `entryPath` back at it once it earned its
 * place. Custom reports are that.
 *
 * **"Standard reports" and "My reports" list them**, live, through
 * `useRows`: the tenant's standard reports in their own order, then the
 * reader's own, A–Z. Each row's own path is a group key that redirects to the
 * Overview, so with no reports yet it is a link to the page that starts one
 * rather than an empty route.
 *
 * The catalog's sidebar and routes are still **derived from the report
 * registry** (`pages/reports/report-registry.tsx`): a canned report is one
 * entry there.
 *
 * Shared records aren't listed here, and this module shows no record bar
 * (`recordBar` unset, AB#8790). A report row that links to a client or deal
 * still opens it, since record routes register for every module.
 */

const ReportsPage = lazy(() =>
  import("@/pages/reports/ReportsPage").then((m) => ({
    default: m.ReportsPage,
  })),
);
const ReportBuilderPage = lazy(() =>
  import("@/pages/reports/builder/ReportBuilderPage").then((m) => ({
    default: m.ReportBuilderPage,
  })),
);
const ReportRenderPage = lazy(() =>
  import("@/pages/reports/builder/ReportRenderPage").then((m) => ({
    default: m.ReportRenderPage,
  })),
);

/**
 * The module's settings gear: one toggle, whether scheduled report emails
 * reach internal staff in a lower environment. Declared everywhere but
 * production, whose mail is never guarded. The environment is the hostname's,
 * fixed for the page's life, so deciding it at module load is safe; the
 * server 404s the setting in production regardless.
 */
const settingsToggles: ModuleSettingsToggle[] =
  detectEnvironment().label === "Prod"
    ? []
    : [{ key: "reports-staff-email", element: <StaffEmailToggle area="reports" /> }];

/** One sidebar entry per catalog report. The composition layer sorts A–Z by label. */
const catalogRoutes: AppRoute[] = REPORTS.map<AppRoute>((r) => ({
  path: reportPath(r.slug),
  label: r.label,
  icon: r.icon,
  status: "live",
  element: r.element,
  requiredPermission: r.requiredPermission,
  requiredFeature: r.requiredFeature,
  requiresSuperAdmin: r.requiresSuperAdmin,
}));

export const REPORTS_MODULE: ModuleDefinition = {
  key: "reports",
  label: "Reports",
  icon: FileBarChart,
  entryPath: REPORTS_INDEX_PATH,
  // Reports predates module access: its existing `reports.view` is the key.
  requiredPermission: "reports.view",

  sections: [
    {
      // No label: the module switcher directly above already says "Reports".
      key: "reports-home",
      // Overview first: it is the module's home, and the report lists sit
      // beneath it, not peers to find alphabetically.
      preserveOrder: true,
      useRows: useMyReportRows,
      routes: [
        {
          path: REPORTS_INDEX_PATH,
          label: "Overview",
          icon: LayoutGrid,
          status: "live",
          element: <ReportsPage />,
          title: "Reports",
          breadcrumbs: [{ label: "Reports" }],
          requiredPermission: "reports.view",
        },
        {
          path: STANDARD_REPORTS_PATH,
          label: "Standard reports",
          icon: BookMarked,
          status: "live",
          element: <Navigate to={REPORTS_INDEX_PATH} replace />,
          requiredFeature: "report_builder",
          requiredPermission: "reports.view",
        },
        {
          path: MY_REPORTS_PATH,
          label: "My reports",
          icon: FolderOpen,
          status: "live",
          // A real element, so the router registers this path (a pure
          // grouping node would register nothing, and with no reports the
          // row renders as a link). It sends the reader to the Overview.
          element: <Navigate to={REPORTS_INDEX_PATH} replace />,
          requiredFeature: "report_builder",
          requiredPermission: "reports.view",
        },
      ],
    },
    // Only when the registry holds a report: an empty group is a heading
    // over nothing.
    ...(catalogRoutes.length > 0
      ? [{ key: "reports-catalog", label: "Catalog", routes: catalogRoutes }]
      : []),
  ],

  settingsToggles,

  hiddenRoutes: [
    {
      path: `${MY_REPORTS_PATH}/:id`,
      label: "Report",
      status: "live",
      element: <ReportBuilderPage />,
      title: "Report",
      requiredFeature: "report_builder",
      requiredPermission: "reports.view",
    },
    {
      // A PDF report drawn with no chrome for the server's headless browser
      // to capture: scheduled delivery. Never linked; see ReportRenderPage.
      path: `${MY_REPORTS_PATH}/:id/render`,
      label: "Report",
      status: "live",
      element: <ReportRenderPage />,
      title: "Report",
      bare: true,
      requiredFeature: "report_builder",
      requiredPermission: "reports.view",
    },
  ],
};
